import { dateFormat } from '@constants/dateFormat'
import { shifts } from '@constants/orderDispatch'
import { Texto } from '@gravitate-js/excalibrr'
import api from '@utils/api'
import { formatters } from '@utils/intl/dateTimeFormatters'
import classNames from 'classnames'
import _ from 'lodash'
import moment, { type Moment, type MomentInput, type unitOfTime } from 'moment'
import React, { type MutableRefObject, useEffect, useRef } from 'react'

import { formatTimeWindow } from '../components/_SiteWide/OrderDisplay/OrderTimeFormats'
// TODO: Replace definitions with types in the Gravitate. namespace
import type { Destination, Driver, Event, Sector, Shift } from './general.types'

// Aliasing order here because it seems like there's a conflict in usage with the dispatch window fields.
// TODO: Understand true usage and replace in order.d.ts
type Order = Gravitate.Order & {
  dispatch_window_start_date?: Moment | string
  dispatch_window_end_date?: Moment | string
  default_carrier_window_start_local?: Intl.DateTimeFormatOptions['timeZone']
}

export const clamp = (num: number, min: number, max: number) => Math.min(Math.max(num, min), max)

export const getCurrentMarket = (): string => {
  return localStorage.currentMarket
}

export const setCurrentMarket = (value: string) => {
  localStorage.currentMarket = value
}

export const getShowNetworkTimezone = (args?: { timeZone: Intl.DateTimeFormatOptions['timeZone'] }) => {
  // Added additional optional args param for moment.tz to prevent potential crashes for undefined timezones
  if (args && args.timeZone === undefined) return false
  return localStorage.getItem('USE_NETWORK_TIMEZONE') === 'true'
}

export const setShowNetworkTimezone = (value: boolean) => {
  localStorage.setItem('USE_NETWORK_TIMEZONE', value?.toString() ?? 'false')
}

export const getDisplayTimezoneString = () => {
  return localStorage.getItem('DISPLAY_TIMEZONE_STRING') === 'true'
}

export const setDisplayTimezoneString = (value: string) => {
  localStorage.setItem('DISPLAY_TIMEZONE_STRING', value?.toString() ?? 'false')
}

export function getTimeDisplayPreference() {
  const showNetworkTime = localStorage.getItem('USE_NETWORK_TIMEZONE') === 'true'
  const showTimezoneString = localStorage.getItem('DISPLAY_TIMEZONE_STRING') === 'true'
  return showTimezoneString ? 'NETWORK_WITH_TIMEZONE' : showNetworkTime ? 'NETWORK' : 'LOCAL'
}

export type TimeDisplayPreference = ReturnType<typeof getTimeDisplayPreference>

export const getNameInitials = (name: string) => {
  return name.indexOf(' ') !== -1
    ? name
        .split(' ')
        .map((n) => n[0])
        .join('')
    : name.substring(0, 1)
}

export const getMarketSectors = (values: string[] = [], sectors: Sector[]) => {
  if (!sectors) return []
  const markets = sectors.map((s) => s.name)
  const sectorList = [] as string[]
  values?.length > 0 &&
    values.forEach((v) => {
      if (markets.includes(v)) {
        const match = sectors.filter((sector) => sector.name.toLowerCase() === v.toLowerCase())
        if (match.length > 0) {
          const marketSectors = match[0].sectors.map((s) => s.name)
          sectorList.push(...marketSectors)
        }
      } else sectorList.push(v)
    })
  return sectorList
}

export const convertStorageStringToArray = (value: Maybe<string>, returnUndefined: boolean) => {
  if (!value || value === '') return returnUndefined ? undefined : []
  return value.includes(',') ? value.split(',') : [value]
}

export const titleCase = (string: string) => {
  const words = string.toLowerCase().split('_')
  for (let i = 0; i < words.length; i++) {
    words[i] = words[i][0].toUpperCase() + words[i].slice(1)
  }

  return words.join(' ')
}

export const sortNumeric = (data: string[], property: string, sortDirection = 'asc') => {
  const compareSettings = {
    numeric: true,
    sensitivity: 'base',
  } as Intl.CollatorOptions

  if (!property) {
    return data?.sort((a, b) => a.localeCompare(b, undefined, compareSettings))
  }
  if (sortDirection === 'asc') {
    return data?.sort((a, b) => a[property].localeCompare(b[property], undefined, compareSettings))
  }

  return data?.sort((a, b) => b[property].localeCompare(a[property], undefined, compareSettings))
}

export const sortDestinations = (current: Destination, next: Destination) => {
  const currentLocation = parseInt(current.drops[0].location_name)
  const nextLocation = parseInt(next.drops[0].location_name)

  if (isNaN(currentLocation) && isNaN(nextLocation)) {
    return currentLocation > nextLocation ? 1 : -1
  }

  if (!isNaN(currentLocation) && !isNaN(nextLocation)) {
    return currentLocation > nextLocation ? 1 : -1
  }

  if (isNaN(currentLocation) && !isNaN(nextLocation)) {
    return 1
  }

  if (!isNaN(currentLocation) && isNaN(nextLocation)) {
    return -1
  }
}

// antd column config generator method
export const antDColumns = (
  data: any,
  overrides = {},
  ignoredColumns = [],
  extraColumns = [],
  columnOrder = [],
  explicitColumns = []
) => {
  if (!data || data.length === 0) {
    return []
  }

  const ignore = (ignoredColumns || []).concat(['key'])
  const firstRow = data[0]
  const cols = Object.keys(firstRow)
    .filter((k) => !ignore.includes(k))
    .filter((k) => explicitColumns.length === 0 || explicitColumns.includes(k))
    .map((k) => ({ title: titleCase(k), dataIndex: k, key: k, ...overrides[k] }))
    .concat(extraColumns)
    .map((o) => {
      const orderedIndex = columnOrder.indexOf(o.key)
      return { ...o, order: orderedIndex > -1 ? orderedIndex : undefined }
    })

  return _.orderBy(cols, 'order')
}

export const initialLastName = (name: string) => {
  const fullName = name.split(' ')
  return `${fullName.shift()} ${fullName.pop()?.charAt(0)}.`
}

export const addDispatchWindows = (orders: Order[]) => {
  const now = moment()
  orders.forEach((order) => {
    const dispatchWindowStart = moment(order.dispatch_window_start)
    order.dispatch_window_start_date = dispatchWindowStart.isBefore(now) ? '' : dispatchWindowStart
    order.dispatch_window_end_date = moment(order.dispatch_window_end)
  })
}

export const isBeforeNow = (dateTime: MomentInput) => {
  return moment(dateTime).isBefore(moment())
}

export const getNextWeekdayDate = (dayIndex: number) => {
  const today = moment().isoWeekday()

  if (today <= dayIndex) {
    return moment().isoWeekday(dayIndex)
  }
  return moment().add(1, 'weeks').isoWeekday(dayIndex)
}

export function ReadableDifference(
  dateTo: MomentInput,
  dateFrom = moment().local(),
  forRunout?: boolean,
  isBackhaul?: boolean,
  rightAlign?: boolean
) {
  if (dateTo == null) {
    return '---'
  }
  const dateMoment = moment(dateTo).local()
  if (!dateMoment.isValid()) {
    return '---'
  }
  const seconds = dateMoment.diff(dateFrom, 'seconds')

  if (forRunout && seconds <= 0) {
    if (isBackhaul) return 'PAST DELIVERY'

    const timeSinceRunout = getTimeSinceRunout(dateMoment, moment(dateFrom))

    return (
      <div
        className={classNames('flex-div', { 'items-center': !rightAlign })}
        style={{
          flexWrap: 'wrap',
          justifyContent: rightAlign ? 'flex-end' : '',
          alignItems: rightAlign ? 'flex-end' : '',
        }}
      >
        {/** @ts-ignore */}
        <Texto className='pr-1' appearance='error' weight='bold' align={rightAlign && 'right'}>
          EMPTY
        </Texto>
        {/** @ts-ignore */}
        <Texto appearance='error' category='label' align={rightAlign && 'right'}>
          -{timeSinceRunout}
        </Texto>
      </div>
    )
  }
  return humanReadableSecondDifferenceFromToday(seconds)
}

export const getTimeSinceRunout = (runoutDate: MomentInput, dateFrom: Moment) => {
  const minutesDiff = dateFrom.diff(runoutDate, 'minutes')
  const hoursDiff = dateFrom.diff(runoutDate, 'hours')

  const numDays = Math.floor(hoursDiff / 24)
  const numHours = Math.floor((minutesDiff % 1440) / 60)
  const numMinutes = Math.floor((minutesDiff % 1440) % 60)
  if (hoursDiff >= 24) {
    return `${numDays}d ${numHours}h ${numMinutes}m`
  }
  if (numHours > 0) {
    return `${numHours}h ${numMinutes}m`
  }
  return `${numMinutes}m`
}

export const getTimeUntilRetain = (retainDate: Moment) => {
  const now = moment()

  // If retainDate is in the past, its ready to go.
  if (now.isSameOrAfter(retainDate)) {
    return 'Fits Now'
  }

  const minutesDiff = retainDate.diff(now, 'minutes')
  const hoursDiff = retainDate.diff(now, 'hours')

  const numDays = Math.floor(hoursDiff / 24)
  const numHours = Math.floor((minutesDiff % 1440) / 60)
  const numMinutes = Math.floor((minutesDiff % 1440) % 60)

  if (hoursDiff >= 24) {
    return `${numDays}d ${numHours}h ${numMinutes}m`
  }
  if (numHours > 0) {
    return `${numHours}h ${numMinutes}m`
  }
  return `${numMinutes}m`
}

export const convertTimeSpanToReadable = (timespan: string) => {
  const a = timespan.split(':')
  const seconds = +a[0] * 60 * 60 + +a[1] * 60 + +a[2]

  return humanReadableSecondDifferenceFromToday(seconds)
}

export const getCarrierDeliveryWindow = (order: Order) => {
  if (!order.carrier_window_start)
    return formatTimeWindow(
      order.default_carrier_window_start,
      order.default_carrier_window_end,
      false,
      false,
      order.default_carrier_window_start_local
    )
  return formatTimeWindow(
    order.carrier_window_start,
    order.carrier_window_end,
    false,
    false,
    order.default_carrier_window_start_local
  )
}

export const filterDriversByHomeLocation = (drivers: Driver[], depots: string[]) => {
  return drivers.filter((d) => depots.includes(d.home_location))
}

export const getEventsFromScheduleData = (scheduleData: any[], shift: string) => {
  const isPm = shift === shifts.PM
  const events = []

  for (let i = 0, dataLength = scheduleData.length; i < dataLength; i++) {
    for (let j = 0, len = scheduleData[i].trips.length; j < len; j++) {
      if (scheduleData[i].trips[j].order_number !== -1) {
        const index = `${i}${j}`
        pushEvent(scheduleData[i].id, index, isPm, scheduleData[i].trips[j], events)
      }
    }
  }

  return events
}

export const formatDateStringToTime = (string: string, timeZone?: Intl.DateTimeFormatOptions['timeZone']) => {
  return formatters.TIME(moment(string), timeZone)
}

export const formatDateStringToReadable = (string: string, timeZone?: Intl.DateTimeFormatOptions['timeZone']) => {
  return formatters.DATE_TIME(moment(string), timeZone)
}

export const isPastForever = (date: MomentInput) => {
  const forever = moment('2100-01-01')
  return moment(date).isAfter(forever)
}

export const humanReadableSecondDifferenceFromToday = (seconds: number) => {
  const showAgo = seconds < 0
  seconds = Math.abs(seconds)
  const numdays = Math.floor(seconds / 86400)
  const numhours = Math.floor(((seconds % 31536000) % 86400) / 3600)
  const numminutes = Math.floor((((seconds % 31536000) % 86400) % 3600) / 60)

  const daysString = numdays !== 0 ? `${Math.abs(numdays)}d ` : ''
  const hoursString = numhours !== 0 ? `${Math.abs(numhours)}h ` : ''
  const minutesString = numminutes > 0 && numdays <= 0 ? `${numminutes.toFixed(0)}m ` : ''

  return `${daysString}${hoursString}${minutesString}${showAgo ? 'ago' : ''}`
}

export const translateDateStringToToday = (stringOrDate: string | Date, isPm: boolean) => {
  const pmAdjust = isPm ? 12 : 0

  if (stringOrDate) {
    const dateVersion = stringOrDate instanceof Date ? stringOrDate : new Date(stringOrDate)
    const today = new Date()
    let hoursAdjust = dateVersion.getHours() - pmAdjust

    if (hoursAdjust < 0) {
      hoursAdjust += 24
    }

    today.setHours(hoursAdjust)
    today.setMinutes(dateVersion.getMinutes())
    today.setSeconds(dateVersion.getSeconds())

    return today.toISOString()
  }
}

export const pushEvent = (id: string, index: string | number, isPm: boolean, trip: Gravitate.Trip, events: Event[]) => {
  events.push({
    id: `${index}-E`,
    resourceId: `Estimate-${id}`,
    title: String(trip.number),
    start: translateDateStringToToday(trip.estimated_start, isPm),
    end: translateDateStringToToday(trip.estimated_end, isPm),
  })

  if (trip.actual_start_time) {
    events.push({
      id: `${index}-A`,
      resourceId: `Actual-${id}`,
      title: String(trip.number),
      start: translateDateStringToToday(trip.actual_start_time, isPm),
      end: translateDateStringToToday(trip.actual_end_time, isPm),
    })
  }
}

export const getElapsedTime = (
  dateString: MomentInput,
  relativeTimeCutoff: number,
  relativeTimeUnits?: unitOfTime.Diff,
  nonRelativeDateFormat?: string
) => {
  moment.locale('en', {
    relativeTime: {
      future: 'in %s',
      past: '%s ago',
      s: 's',
      m: '1 min',
      mm: '%d min',
      h: '1 h',
      hh: '%d h',
      d: '1 d',
      dd: '%d d',
      M: '1 mth',
      MM: '%d mth',
      y: '1 y',
      yy: '%d y',
    },
  })
  const date = moment(dateString)
  const elapsedSeconds = moment(dateString).diff(moment(), relativeTimeUnits)
  if (Math.abs(elapsedSeconds) <= relativeTimeCutoff) {
    return date.fromNow()
  }
  return date.format(nonRelativeDateFormat)
}

export const useInterval = (callback: Function, delay: number) => {
  const savedCallback = useRef<Function>()

  useEffect(() => {
    savedCallback.current = callback
  }, [callback])

  useEffect(() => {
    const tick = () => {
      if (savedCallback.current) {
        savedCallback.current()
      }
    }
    if (delay) {
      const id = setInterval(tick, delay)
      return () => clearInterval(id)
    }
  }, [delay])
}

export const getPercentageColor = (value: number, lowThreshold: number, mediumThreshold: number) => {
  if (value < lowThreshold) return 'red'
  if (value < mediumThreshold) return 'blue'
  return 'green'
}

export const deltaToLowColor = (value: number, lowValue: number) => {
  if (value > lowValue) return 'red'
  return 'green'
}

export const ArrayRemove = <T,>(arr: Array<T>, value: T) => {
  for (let i = 0; i < arr.length; i++) {
    if (arr[i] === value) {
      arr.splice(i, 1)
      return
    }
  }
}

export const ShowRunOutWarningClass = (estimated_run_out: moment.MomentInput) => {
  const runOutDate = moment(estimated_run_out)
  const tommorrow = moment().utc().add(1, 'd')
  const veryClose = moment().utc().add(6, 'h')
  if (runOutDate <= veryClose) return 'run-out-error'
  if (runOutDate <= tommorrow) return 'run-out-warning'
  return ''
}

export const addDriverScheduleEvent = (
  key: string,
  driver,
  drivers,
  scheduleDate,
  setDrivers,
  setDriverSchedule,
  driverSchedule,
  rateType
) => {
  const copyDrivers = [...drivers]
  const copy = copyDrivers.filter((d) => d.name.toLowerCase() === driver.name.toLowerCase())[0]
  const shifts = copy.schedule.filter(
    (d) => moment.utc(d.date).format(dateFormat.DATE) === moment.utc(scheduleDate).format(dateFormat.DATE)
  )
  const grabDriverShiftDefault = () => {
    const startTimeString =
      key.toLowerCase() === 'am shift'
        ? driver.am_shift_start || driver.am_shift.start_time
        : driver.pm_shift_start || driver.pm_shift.start_time

    const startDate = moment(scheduleDate)
    const startTime = moment(startTimeString)
    startDate.set({
      hour: startTime.get('hour'),
      minute: startTime.get('minute'),
      second: startTime.get('second'),
    })
    const endTime = moment(startDate).add(driver.shift_length.shift_length_hours, 'hours')
    return { startTime: startDate.toISOString(), endTime: endTime.toISOString() }
  }
  if (shifts.length > 0) {
    let start_date = null
    let end_date = null
    if (['am shift', 'pm shift'].includes(key.toLowerCase())) {
      const dates = grabDriverShiftDefault()
      start_date = dates.startTime
      end_date = dates.endTime
    } else {
      start_date = moment(scheduleDate).set({ hour: 8, minute: 0, second: 0, millisecond: 0 }).format(dateFormat.ISO)
      end_date = moment(scheduleDate).set({ hour: 18, minute: 0, second: 0, millisecond: 0 }).format(dateFormat.ISO)
    }

    const event = {
      driver,
      carrier_id: driver.carrier_id,
      carrier_resource_num: driver.carrier_resource_num,
      depot: driver.home_location,
      type: key,
      date: scheduleDate,
      start_date,
      end_date,
      rate_type: rateType,
    }

    api.createEvent(event).then(({ data }) => {
      const shift = shifts[0]

      const event = {
        id: data.id,
        type: key,
        start_date: data.start_date,
        end_date: data.end_date,
        rate_type: rateType,
      }

      shift.events.push(event)

      if (event.type === 'Maintenance') copy.next_tractor_maintenance_required = false

      setDrivers(copyDrivers)
      UpdateDriverAllocation(copyDrivers, scheduleDate, driverSchedule, setDriverSchedule)
    })
  }
}

export const getTimezone = () => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone
}

export const GetNextSevenDaysAndDates = () => {
  const today = moment()
  const weekDays = [{ day: today.format(dateFormat.DAY), dateString: today.format(dateFormat.DATE) }]
  let days = 0

  while (days < 6) {
    days++
    const nextDay = today.add(1, 'd')
    weekDays.push({ day: nextDay.format(dateFormat.DAY), dateString: nextDay.format(dateFormat.DATE) })
  }

  return weekDays
}

export const GetAmStartDate = (date: MomentInput) => {
  return moment(date).set({ hour: 4, minute: 30, second: 0, millisecond: 0 }).format(dateFormat.ISO)
}

export const GetAmEndDate = (date: MomentInput) => {
  return moment(date).set({ hour: 16, minute: 30, second: 0, millisecond: 0 }).format(dateFormat.ISO)
}

export const GetPmStartDate = (date: MomentInput) => {
  return moment(date).set({ hour: 16, minute: 30, second: 0, millisecond: 0 }).format(dateFormat.ISO)
}

export const GetPmEndDate = (date: MomentInput) => {
  return moment(date).add(1, 'd').set({ hour: 4, minute: 30, second: 0, millisecond: 0 }).format(dateFormat.ISO)
}

export function roundToNearestHalf(number) {
  return Math.round(number * 2) / 2
}

export const UpdateDriverAllocation = (
  drivers: Driver[], // TODO: type driver object
  scheduleDate: MomentInput,
  driverSchedule: Shift[], // TODO: type driver schedule object
  setDriverSchedule: (schedule: any) => void
) => {
  if (setDriverSchedule) {
    let amShifts = 0
    let pmShifts = 0

    for (let i = 0; i < drivers.length; i++) {
      const currentDriver = drivers[i]
      const shifts = currentDriver.schedule.filter(
        (d) => moment.utc(d.date).format(dateFormat.DATE) === moment.utc(scheduleDate).format(dateFormat.DATE)
      )

      if (shifts.length > 0) {
        const shift = shifts[0]
        for (let j = 0; j < shift.events.length; j++) {
          const currentEvent = shift.events[j]
          if (currentEvent.type.toLowerCase() === 'am shift') amShifts++
          if (currentEvent.type.toLowerCase() === 'pm shift') pmShifts++
        }
      }
    }

    const copyDriverSchedule = [...driverSchedule]

    const driverScheduleDates = copyDriverSchedule.filter(
      (d) => moment.utc(d.date).format(dateFormat.DATE) === moment.utc(scheduleDate).format(dateFormat.DATE)
    )

    if (driverScheduleDates.length > 0) {
      const firstDate = driverScheduleDates[0]
      firstDate.am_driver_allocated = amShifts
      firstDate.pm_driver_allocated = pmShifts
      setDriverSchedule([...copyDriverSchedule])
    }
  }
}

export const SetDriverResourceWeekSpan = (dateString: MomentInput) => {
  const filter = { start_date: '', end_date: '' }
  const startDate = moment.utc(dateString)

  filter.start_date = startDate
    .startOf('week')
    .set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
    .format(dateFormat.ISO)

  filter.end_date = startDate
    .endOf('week')
    .set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
    .format(dateFormat.ISO)

  return filter
}

export const GetDuration = (date: MomentInput) => {
  const now = moment().utc().local()
  const updated = moment.utc(date).local()
  return moment.duration(now.diff(updated))
}

export const TankHasWarning = (estimated_run_out: MomentInput) => {
  const runOutDate = moment.utc(estimated_run_out).local()
  const now = moment().add(1, 'd')
  return runOutDate <= now
}

export const triggerGridRead = (gridReference: MutableRefObject<any>, updatedRecord: any) => {
  if (updatedRecord) {
    gridReference && gridReference.current?.dataSource.updateRow(updatedRecord)
  } else {
    const readFunc = _.get(gridReference, 'current.dataSource.read', () => window.location.reload)
    readFunc()
  }
}

export const removeGridRows = (gridReference: MutableRefObject<any>, rowsToRemove: any[]) => {
  if (rowsToRemove) {
    gridReference && gridReference.current?.dataSource.removeRows(rowsToRemove)
  } else {
    const readFunc = _.get(gridReference, 'current.dataSource.read', () => window.location.reload)
    readFunc()
  }
}

type Maybe<T> = T | undefined | null

export function isDefined<T>(v: Maybe<T>): v is T {
  return typeof v !== 'undefined' && v !== null
}

export function useDebugState<T>(value: T | (() => T)): [T, React.Dispatch<React.SetStateAction<T>>] {
  const [state, setState] = React.useState(value)

  React.useEffect(() => {
    console.log('State:', state)
  }, [state])

  return [state, setState]
}

export function sortStringNumbers(stringOrNumber: (string | number)[]) {
  // Sort strings that contain numbers correctly, ie. sort store numbers
  // storenumbersorter storesort sortstore
  return stringOrNumber.sort((a, b) => {
    const isANumber = typeof a === 'number'
    const isBNumber = typeof b === 'number'

    if (isANumber && isBNumber) {
      return a - b
    }
    if (isANumber) {
      return -1
    }
    if (isBNumber) {
      return 1
    }
    // Both are strings, sort them lexicographically
    return a.toString().localeCompare(b.toString())
  })
}

export function getLocalStorage(key: string) {
  try {
    const value = localStorage.getItem(key)
    if (!value) return null
    return JSON.parse(value)
  } catch (e) {
    console.error('Error parsing grid view list from local storage:', e)
  }
}

export function setLocalStorage<T>(key: string, value: T) {
  if (!value) return null
  try {
    const stringValue = JSON.stringify(value)
    return localStorage.setItem(key, stringValue)
  } catch (e) {
    console.error('Error parsing grid view list from local storage:', e)
  }
}

/**
 *
 * @param jsonString Catch JSON parsing errors and return null if the string is not valid JSON, preventing exceptions from being thrown.
 * @returns Parsed JSON object as type T or null if parsing fails.
 */
export function jsonSafeParse<T>(jsonString: string): T | null {
  try {
    return JSON.parse(jsonString) as T
  } catch (e) {
    console.error('Error parsing JSON string:', e)
    return null
  }
}
